import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import {
  addPersonalExpense,
  deletePersonalExpense,
  getMemberFinance,
  updatePersonalExpense,
  type MemberExpenseType,
  type MemberFinanceData,
} from '../../api';
import { useAuth } from '../../hooks/useAuth';
import { useAircraft } from '../../aircraft/AircraftContext';
import { useFeedback } from '../../hooks/useFeedback';
import { useLoadMore } from '../../hooks/useLoadMore';
import { DateFormat, DateFormatStyle } from '../../utils/DateFormat';
import { formatMoney } from '../../utils/formatMoney';
import { downloadExpensesCsv } from '../../utils/exportExpensesCsv';
import { getApiErrorMessage } from '../../utils/apiError';
import {
  MEMBER_EXPENSE_CATEGORY_ROWS,
  MEMBER_EXPENSE_TYPE_OPTIONS,
  expenseTypeLabel,
} from '../../utils/expenseTypes';
import {
  SortConfig,
  SortValueKind,
  defaultDirectionForKind,
  sortByConfig,
  toggleSortConfig,
} from '../../utils/tableSort';
import AddExpenseModal, {
  type PersonalExpenseFormPayload,
} from '../../components/AddExpenseModal/AddExpenseModal';
import ConfirmModal from '../../components/ConfirmModal/ConfirmModal';
import RecordDetailsModal from '../../components/RecordDetailsModal/RecordDetailsModal';
import LoadMoreButton from '../../components/LoadMoreButton/LoadMoreButton';
import SortableTh from '../../components/SortableTh/SortableTh';
import { parseProfileUserId, profileBasePath } from './profilePaths';
import '../AircraftPage/AircraftPage.css';
import '../IssuesPage/IssuesPage.css';
import '../FlightsHistoryPage/FlightsHistoryPage.css';
import '../FinancePage/FinancePage.css';
import './ProfilePage.css';

type ItemSortKey = 'expense_date' | 'expense_type' | 'amount';

type MemberFinanceItem = MemberFinanceData['items'][number] & {
  id: string | number;
  expense_date: string;
  expense_type?: MemberExpenseType;
};

const isPersonalItem = (item: { source_kind?: string }) =>
  item.source_kind === 'personal';

const emptyTotals = () => ({
  fuel: 0,
  oil: 0,
  to: 0,
  repair: 0,
  modernization: 0,
  basing: 0,
  parts: 0,
  airnav: 0,
  other: 0,
  personal: 0,
  total: 0,
});

const emptyMember = (): MemberFinanceData => ({
  user_id: 0,
  aircraft_id: 0,
  year_start: '',
  hours_all: 0,
  hours_year: 0,
  hour_cost_all: null,
  hour_cost_year: null,
  hours: 0,
  hour_cost: null,
  total: 0,
  by_category: emptyTotals(),
  categories: { all: emptyTotals(), year: emptyTotals() },
  items: [],
});

const sortKind = (key: ItemSortKey): SortValueKind => {
  switch (key) {
    case 'expense_date':
      return 'date';
    case 'amount':
      return 'number';
    default:
      return 'string';
  }
};

const ProfileFinancePage = () => {
  const { userId: userIdParam } = useParams();
  const { currentUserId, user, isAdmin, isTechnician } = useAuth();
  const { aircraftId, dataRevision } = useAircraft();
  const { showError, showSuccess, feedbackNode } = useFeedback();

  const paramId = parseProfileUserId(userIdParam);
  const targetUserId = paramId ?? currentUserId;
  const isOwn = targetUserId === currentUserId;
  const basePath = profileBasePath(targetUserId, currentUserId);
  const accessDenied =
    (Boolean(userIdParam) && paramId == null) || (!isOwn && !isAdmin);

  const [data, setData] = useState<MemberFinanceData>(emptyMember());
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState<MemberExpenseType | ''>('');
  const [sortConfig, setSortConfig] = useState<SortConfig<ItemSortKey>>({
    key: 'expense_date',
    direction: 'desc',
  });
  const [adding, setAdding] = useState(false);
  const [viewing, setViewing] = useState<MemberFinanceItem | null>(null);
  const [editing, setEditing] = useState<MemberFinanceItem | null>(null);
  const [deleting, setDeleting] = useState<MemberFinanceItem | null>(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const dateFormat: DateFormatStyle =
    user?.date_format === 'long' ? 'long' : 'dot';
  const canAddPersonal = isOwn || isAdmin;

  const loadedOnce = useRef(false);
  useEffect(() => {
    loadedOnce.current = false;
  }, [targetUserId, aircraftId]);
  const load = useCallback(async () => {
    if (accessDenied || (isTechnician && isOwn)) return;
    const showSpinner = !loadedOnce.current;
    if (showSpinner) setLoading(true);
    try {
      if (aircraftId <= 0) {
        setData(emptyMember());
        return;
      }
      const res = await getMemberFinance(targetUserId);
      setData({
        ...emptyMember(),
        ...res.data,
        by_category: {
          ...emptyTotals(),
          ...(res.data.by_category || {}),
        },
        categories: {
          all: {
            ...emptyTotals(),
            ...(res.data.categories?.all || res.data.by_category || {}),
          },
          year: {
            ...emptyTotals(),
            ...(res.data.categories?.year || {}),
          },
        },
        items: res.data.items || [],
      });
      loadedOnce.current = true;
    } catch (error) {
      showError(getApiErrorMessage(error, 'Ошибка загрузки финансов'));
    } finally {
      if (showSpinner) setLoading(false);
    }
  }, [accessDenied, aircraftId, targetUserId, isTechnician, isOwn, showError]);

  useEffect(() => {
    void load();
  }, [load, dataRevision]);

  const handleSort = (key: ItemSortKey) => {
    setSortConfig((prev) =>
      toggleSortConfig(prev, key, defaultDirectionForKind(sortKind(key)))
    );
  };

  const handleAddPersonal = async (payload: PersonalExpenseFormPayload) => {
    try {
      await addPersonalExpense(
        payload,
        isOwn ? undefined : targetUserId
      );
      setAdding(false);
      showSuccess('Личный расход добавлен');
      await load();
    } catch (error) {
      showError(getApiErrorMessage(error, 'Ошибка добавления расхода'));
    }
  };

  const personalUserId = isOwn ? undefined : targetUserId;

  const handleUpdatePersonal = async (payload: PersonalExpenseFormPayload) => {
    if (!editing) return;
    try {
      await updatePersonalExpense(editing.id, payload, personalUserId);
      setEditing(null);
      showSuccess('Расход обновлён');
      await load();
    } catch (error) {
      showError(getApiErrorMessage(error, 'Ошибка изменения расхода'));
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleting) return;
    setDeleteBusy(true);
    try {
      await deletePersonalExpense(deleting.id, personalUserId);
      setDeleting(null);
      showSuccess('Расход удалён');
      await load();
    } catch (error) {
      showError(getApiErrorMessage(error, 'Ошибка удаления расхода'));
    }
    setDeleteBusy(false);
  };

  const sortedItems = useMemo(() => {
    const filtered = (
      typeFilter
        ? data.items.filter(
            (item) => (item.expense_type || item.category) === typeFilter
          )
        : data.items
    ).map((item, index) => ({
      ...item,
      id:
        item.id ??
        `${item.source_kind}-${item.source_id}-${index}`,
      expense_date: item.expense_date || item.entry_date || '',
      expense_type: item.expense_type || item.category,
    }));
    return sortByConfig(
      filtered,
      sortConfig,
      (row, key) => row[key],
      sortKind
    );
  }, [data.items, sortConfig, typeFilter]);

  const { visibleCount, hasMore, remaining, nextBatch, loadMore, loadAll } = useLoadMore(
    sortedItems.length,
    {
      resetKey: `${sortConfig.key}-${sortConfig.direction}-${typeFilter}`,
    }
  );
  const visibleItems = sortedItems.slice(0, visibleCount);

  if (accessDenied) {
    return <Navigate to="/profile" replace />;
  }
  if (isTechnician && isOwn) {
    return <Navigate to="/profile" replace />;
  }

  return (
    <div className="aircraft-page finance-page">
      {feedbackNode}
      <div className="aircraft-hub">
        <div className="aircraft-header">
          <Link to={basePath} className="profile-back-link">
            ← Профиль
          </Link>
          <h2>{isOwn ? 'Мои финансы' : 'Финансы пилота'}</h2>
        </div>

        {loading ? (
          <p className="profile-hint">Загрузка…</p>
        ) : (
          <>
            <div className="aircraft-hub-grid finance-metrics">
              <div className="aircraft-hub-card is-static">
                <span className="aircraft-hub-label">
                  Стоимость лётного часа
                </span>
                <span className="aircraft-hub-value">
                  <span className="aircraft-hub-number">
                    {formatMoney(data.hour_cost_all)}
                    {data.hour_cost_all != null ? (
                      <span className="aircraft-hub-unit">₽</span>
                    ) : null}
                  </span>
                </span>
                <span className="aircraft-hub-footer">за всё время</span>
              </div>
              <div className="aircraft-hub-card is-static">
                <span className="aircraft-hub-label">
                  Стоимость лётного часа
                </span>
                <span className="aircraft-hub-value">
                  <span className="aircraft-hub-number">
                    {formatMoney(data.hour_cost_year)}
                    {data.hour_cost_year != null ? (
                      <span className="aircraft-hub-unit">₽</span>
                    ) : null}
                  </span>
                </span>
                <span className="aircraft-hub-footer">за последний год</span>
              </div>
            </div>

            {canAddPersonal ? (
              <button
                type="button"
                className="page-cta-btn"
                onClick={() => setAdding(true)}
              >
                Добавить личный расход
              </button>
            ) : null}

            <div className="finance-panel">
              <h3>Расходы всего / за год</h3>
              <table className="finance-totals">
                <thead>
                  <tr>
                    <th></th>
                    <th>Всего</th>
                    <th>За год</th>
                  </tr>
                </thead>
                <tbody>
                  {MEMBER_EXPENSE_CATEGORY_ROWS.map((row) => (
                    <tr key={row.key}>
                      <td>{row.label}</td>
                      <td>
                        {formatMoney(data.categories?.all?.[row.key])} ₽
                      </td>
                      <td>
                        {formatMoney(data.categories?.year?.[row.key])} ₽
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="finance-panel finance-history">
              <div className="finance-history-head">
                <h3>История расходов</h3>
                <button
                  type="button"
                  className="btn-export-flights"
                  onClick={() =>
                    downloadExpensesCsv(sortedItems, 'member-expenses', {
                      includeNote: true,
                    })
                  }
                  disabled={sortedItems.length === 0}
                >
                  Скачать CSV
                </button>
                <label className="finance-type-filter">
                  Тип
                  <select
                    value={typeFilter}
                    onChange={(e) =>
                      setTypeFilter(
                        (e.target.value || '') as MemberExpenseType | ''
                      )
                    }
                    aria-label="Фильтр по типу расхода"
                  >
                    <option value="">Все</option>
                    {MEMBER_EXPENSE_TYPE_OPTIONS.map((item) => (
                      <option key={item.value} value={item.value}>
                        {item.label}
                      </option>
                    ))}
                  </select>
                </label>
              </div>

              <div className="flights-history-table-wrap">
                <table className="flights-history-table flights-history-table-compact">
                  <thead>
                    <tr>
                      <SortableTh
                        label="Дата"
                        columnKey="expense_date"
                        sortConfig={sortConfig}
                        onSort={handleSort}
                      />
                      <SortableTh
                        label="Тип"
                        columnKey="expense_type"
                        sortConfig={sortConfig}
                        onSort={handleSort}
                      />
                      <SortableTh
                        label="Сумма"
                        columnKey="amount"
                        sortConfig={sortConfig}
                        onSort={handleSort}
                      />
                    </tr>
                  </thead>
                  <tbody>
                    {visibleItems.length === 0 ? (
                      <tr>
                        <td
                          colSpan={3}
                          style={{
                            textAlign: 'center',
                            padding: '30px',
                            color: '#7f8c8d',
                          }}
                        >
                          Нет записей
                        </td>
                      </tr>
                    ) : (
                      visibleItems.map((item) => (
                        <tr
                          key={item.id}
                          className="flights-history-row"
                          onClick={() => setViewing(item)}
                        >
                          <td>
                            {item.expense_date
                              ? DateFormat(item.expense_date, dateFormat)
                              : '—'}
                          </td>
                          <td className="record-row-main">
                            {expenseTypeLabel(item.expense_type || item.category)}
                          </td>
                          <td>{formatMoney(item.amount)} ₽</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
              {hasMore ? (
                <LoadMoreButton
                  remaining={remaining}
                  nextBatch={nextBatch}
                  onClick={loadMore}
                  onShowAll={loadAll}
                />
              ) : null}
            </div>
          </>
        )}
      </div>

      {adding ? (
        <AddExpenseModal
          variant="personal"
          onClose={() => setAdding(false)}
          onConfirm={handleAddPersonal}
        />
      ) : null}

      {viewing ? (
        <RecordDetailsModal
          title="Расход"
          summary={
            <>
              <span className="record-details-date">
                {viewing.expense_date
                  ? DateFormat(viewing.expense_date, dateFormat)
                  : '—'}
              </span>
              <span className="record-details-lead">
                {expenseTypeLabel(viewing.expense_type || viewing.category)}
              </span>
            </>
          }
          rows={[
            { label: 'Сумма', value: `${formatMoney(viewing.amount)} ₽` },
            {
              label: 'Описание',
              value: viewing.note?.trim() || '—',
            },
          ]}
          actions={
            canAddPersonal && isPersonalItem(viewing)
              ? [
                  {
                    label: 'Изменить',
                    onClick: () => {
                      setEditing(viewing);
                      setViewing(null);
                    },
                  },
                  {
                    label: 'Удалить',
                    onClick: () => {
                      setDeleting(viewing);
                      setViewing(null);
                    },
                    danger: true,
                  },
                ]
              : []
          }
          onClose={() => setViewing(null)}
        />
      ) : null}

      {editing ? (
        <AddExpenseModal
          variant="personal"
          title="Изменить расход"
          confirmLabel="Сохранить"
          initial={{
            expense_date: String(editing.expense_date || '').slice(0, 10),
            amount: editing.amount,
            description: editing.note?.trim() || '',
          }}
          onClose={() => setEditing(null)}
          onConfirm={handleUpdatePersonal}
        />
      ) : null}

      {deleting ? (
        <ConfirmModal
          title="Удалить расход?"
          message={`${deleting.note?.trim() || 'Личный расход'} на ${formatMoney(deleting.amount)} ₽ от ${deleting.expense_date ? DateFormat(deleting.expense_date, dateFormat) : '—'}.`}
          confirmLabel="Удалить"
          danger
          submitting={deleteBusy}
          onCancel={() => {
            if (!deleteBusy) setDeleting(null);
          }}
          onConfirm={() => void handleDeleteConfirm()}
        />
      ) : null}
    </div>
  );
};

export default ProfileFinancePage;
